import { oc } from "@orpc/contract";
import { z } from "zod";
import { ArInvoiceSalespersonSchema } from "./ar-invoices.contract";

/**
 * AR Short-Pay Recommendation RPC Contract
 *
 * An AI agent reviews the evidence assembled on a collection thread (invoice,
 * payments, remittance advices, POD/BOL documents + the emails that carried
 * them, sales order, customer PO) and produces a ranked list of recommended
 * collection actions drawn from the shared ArCollectionAction taxonomy, each
 * grounded in the specific evidence behind it.
 *
 * Auto-generated when a short-pay thread is created; regenerable on demand.
 * Results are cached per-thread with a data fingerprint for staleness.
 */

export const ShortPayRecommendationConfidenceSchema = z.enum([
  "high",
  "medium",
  "low",
]);
export type ShortPayRecommendationConfidence = z.infer<
  typeof ShortPayRecommendationConfidenceSchema
>;

/**
 * A single ranked recommendation. `action` is one of the shared collection
 * actions; `evidence` lists the specific facts (amounts, doc types, dates)
 * the model leaned on so a human can sanity-check the call.
 */
export const ShortPayRecommendationSchema = z.object({
  /**
   * Machine id of the recommended action. Was a locked enum; now a free string
   * validated against the org/customer's configured action catalog at runtime
   * (the prompt is given the allowed ids). The response's `actionCatalog`
   * carries the display label + emailable flag for each id the UI renders.
   */
  action: z.string(),
  confidence: ShortPayRecommendationConfidenceSchema,
  /** 1-2 sentences explaining why this action fits, grounded in the evidence. */
  rationale: z.string(),
  /** Specific evidence the recommendation rests on (e.g. "Remittance cites $1,250 freight deduction", "POD delivered 2026-04-02"). */
  evidence: z.array(z.string()),
});
export type ShortPayRecommendation = z.infer<
  typeof ShortPayRecommendationSchema
>;

/**
 * Full agent output for a thread: a short situational summary plus the ranked
 * recommendations (highest priority first).
 */
export const ShortPayRecommendationResultSchema = z.object({
  /** 2-3 sentence factual summary of the short-pay situation and evidence. */
  summary: z.string(),
  /** Ranked recommended actions, most strongly recommended first. */
  recommendations: z.array(ShortPayRecommendationSchema),
});
export type ShortPayRecommendationResult = z.infer<
  typeof ShortPayRecommendationResultSchema
>;

/**
 * The action id a user can dismiss ("don't suggest this again"). A free string
 * like the recommendation `action` it mirrors — dismissals are stored as a
 * memory-bank note keyed on the id, so any configured/custom action can be
 * muted.
 */
export const DismissibleArCollectionActionSchema = z.string().min(1);

/**
 * A recommendation the user has muted for this thread's customer. Backed by a
 * customer-scoped memory bank note; `noteId` is what the card uses to undismiss
 * (it archives the note via the existing memoryBank.delete endpoint).
 */
export const DismissedArCollectionActionSchema = z.object({
  action: DismissibleArCollectionActionSchema,
  /** Optional free-text reason captured at dismissal time. */
  reason: z.string().nullable(),
  /** Memory bank note id backing this dismissal; used to undismiss. */
  noteId: z.string().uuid(),
});
export type DismissedArCollectionAction = z.infer<
  typeof DismissedArCollectionActionSchema
>;

/**
 * A resolved action from the thread's configured catalog: enough for the card
 * to render an action id it received (label + whether to offer "Draft email").
 * Resolved live from org + per-customer config at response time (not cached),
 * so relabeling an action in Settings is reflected on the next read.
 */
export const ResolvedCollectionActionSchema = z.object({
  id: z.string(),
  label: z.string(),
  emailable: z.boolean(),
});
export type ResolvedCollectionAction = z.infer<
  typeof ResolvedCollectionActionSchema
>;

/**
 * API response: the cached recommendation result plus staleness metadata,
 * mirroring the customer-intelligence response shape.
 */
export const ShortPayRecommendationResponseSchema = z.object({
  /** Cached result; null when no analysis has run yet. */
  recommendation: ShortPayRecommendationResultSchema.nullable(),
  /** ISO datetime of last generation; null if never. */
  generatedAt: z.string().datetime().nullable(),
  /** true when the thread's evidence fingerprint has changed since generation. */
  isStale: z.boolean(),
  /** false when no analysis has ever been run for this thread. */
  hasCachedResult: z.boolean(),
  /**
   * Salesperson responsible for the thread's invoice, resolved server-side
   * from `Invoice.salesPersonContactId` (preferred) with
   * `Customer.primarySalesPersonContactId` as the fallback. Null when no rep
   * is on file. The recommendation card uses this to seed the TO recipient
   * when the user drafts an `email_sales_person` action. `email` may be null
   * when Famous carries the rep by name only.
   */
  salesperson: ArInvoiceSalespersonSchema.nullable(),
  /**
   * Actions the user has muted for this thread's customer (org-wide). The card
   * filters these out of `recommendation.recommendations` and surfaces them in
   * a "Hidden for this customer" footer with an undismiss control.
   */
  dismissedActions: z.array(DismissedArCollectionActionSchema),
  /**
   * The action catalog resolved for this thread (org defaults + per-customer
   * override, filtered to the thread's type and enabled actions). The card
   * looks up each recommended/dismissed action id here for its display label
   * and whether to show a "Draft email" button.
   */
  actionCatalog: z.array(ResolvedCollectionActionSchema),
});
export type ShortPayRecommendationResponse = z.infer<
  typeof ShortPayRecommendationResponseSchema
>;

export const arShortPayRecommendationsContracts = {
  /** Retrieve the cached recommendation for a thread (no AI call). */
  get: oc
    .input(z.object({ threadId: z.string().uuid() }))
    .output(ShortPayRecommendationResponseSchema)
    .route({
      method: "GET",
      summary: "Get short-pay recommendations for a collection thread",
      description:
        "Returns the cached AI-ranked actions (dispute, accept, request documents, contact the rep) for a short-pay collection thread, with evidence, staleness flag, salesperson and muted actions. No AI call.",
    }),
  /** Trigger a fresh AI analysis for a thread and cache it. */
  regenerate: oc
    .input(z.object({ threadId: z.string().uuid() }))
    .output(ShortPayRecommendationResponseSchema)
    .route({
      method: "POST",
      summary: "Regenerate short-pay recommendations",
      description:
        "Runs a fresh AI analysis of a short-pay collection thread's evidence, caches the ranked recommended actions, and returns them.",
    }),
  /**
   * Mute a recommended action for this thread's customer ("don't suggest this
   * again"). Writes a customer-scoped memory bank note and returns the cached
   * recommendation with the action already filtered out. Org-wide: every thread
   * for that customer stops surfacing the action.
   */
  dismissAction: oc
    .input(
      z.object({
        threadId: z.string().uuid(),
        action: DismissibleArCollectionActionSchema,
        /** Optional free-text reason, stored on the note for later review. */
        reason: z.string().trim().max(500).optional(),
      }),
    )
    .output(ShortPayRecommendationResponseSchema)
    .route({
      method: "POST",
      summary: "Dismiss a recommended short-pay action",
      description:
        "Stops suggesting an action for the thread's customer on every thread by saving a customer memory note with optional reason; returns the filtered recommendations.",
    }),
} as const;
